import config from '@/config'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from './App.vue'
import getRouter from './router'
import { initStoreWatchers } from './store'
import { i18n } from '@/i18n'

import VueScrollTo from 'vue-scrollto'
import VueCookies from 'vue3-cookies'
import InlineSvg from 'vue-inline-svg'
import Sentry from '@/modules/Sentry'
import type { Scope } from '@sentry/browser'

import { ENV_DEV_OR_STAGING, VUE_APP_VERSION } from '@/helpers/environment'
import { setDeviceId } from '@/composables/useExperiments'
import Braze from '@/plugins/Braze'
import Amplitude from '@/plugins/Amplitude'
import PriceFormat from '@/plugins/PriceFormat'

import 'animejs' // side-effect import to include in the bundle
import '@/modules/Landing/LandingView.vue'

// Growth UI layout package ships scoped CSS for LayoutScreen /
// LayoutScreenIntermission. The renderer imports tokens + core
// stylesheets but not layout, so pull it in here so sticky scroll
// shapes and the intermission card lift apply on first paint.
import '@simple-life-app/growth-ui-layout/dist/style.css'

declare global {
	interface Window {
		deviceId?: string
	}
}

setDeviceId()

// Handle chunk load failures (network issues, ad blockers).
// Non-critical chunks (consent, analytics, marketing) are silently swallowed.
// Critical chunks (route components, billing) trigger a single page reload.
window.addEventListener('vite:preloadError', (event) => {
	const message = event.payload.message

	const nonCriticalPatterns = [
		'consent-ui',
		'event-schema',
		'vendor-messaging',
		'vendor-sentry',
		// Fallback for old chunk names before rename is deployed
		'onetrust',
		'analytics-',
		'vendor-braze',
	]

	if (nonCriticalPatterns.some((p) => message.includes(p))) {
		event.preventDefault()
		return
	}

	// Critical chunk — reload once, then give up.
	// If chunk_retry is already present, we fall through without preventDefault(),
	// so Vite re-throws the error as an unhandled rejection (intentional "give up").
	const url = new URL(window.location.href)
	if (!url.searchParams.has('chunk_retry')) {
		event.preventDefault()
		url.searchParams.set('chunk_retry', '1')
		window.location.href = url.toString()
	}
})

const app = createApp(App)
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

app.use(pinia)
const router = getRouter()
app.use(router)
app.use(i18n)

app.use(PriceFormat)

app.use(Braze, {
	apiKey: config('BrazeApiKey'),
	baseUrl: config('BrazeSdkEndpoint'),
})

app.use(VueScrollTo)
app.use(VueCookies)
app.component('InlineSvg', InlineSvg)

app.config.errorHandler = function (error, vm, info) {
	Sentry.withScope((scope: Scope) => {
		scope.setExtras({ info })
		scope.setTags({
			vueError: true,
			vueErrorInfo: info,
		})
		scope.captureException(error)
	})
}

// vue-i18n v9 legacy mode sets $t/$n/etc in beforeCreate() via app.mixin().
// Vue 3 blocks writes to $-prefixed properties but the code still works correctly —
// $n falls back to app.config.globalProperties.$n set during i18n install.
// This warnHandler suppresses the resulting noise in dev/staging.
app.config.warnHandler = (msg, _instance, trace) => {
	if (msg.startsWith('Attempting to mutate public property "$')) return
	console.warn(`[Vue warn]: ${msg}\n${trace}`)
}

app.use(Amplitude, {
	options: {
		apiKey: config('AmplitudePaltaWeb'),
		browserOptions: {
			serverUrl: 'https://telemetry.dataplatform.simple.life/v2/amplitude',
			appVersion: VUE_APP_VERSION,
			deviceId: window.deviceId,
			disableCookies: true,
		},

		persistedProperties: {
			onboarding_version: VUE_APP_VERSION,
		},

		onError(error: { code: number; event: unknown; message: string }) {
			if (error.code === 0) return
			Sentry.withScope((scope: Scope) => {
				scope.setExtras({
					event: error.event,
				})
				scope.setTags({
					analyticErrorCode: error.code,
					analyticErrorMessage: error.message,
				})
				scope.captureMessage(error.message)
			})
		},
		logger: ENV_DEV_OR_STAGING,
	},
	router,
})

initStoreWatchers()

app.mount('#app')
